// The departments of a production (docs/brand/BRAND.md). The nine core departments are the spine: every
// production has them, and the logo, the fixed strip in marketing and the spectrum line are about them (`CORE`).
// A production may add an extra department (`EXTRAS`): it belongs to a core one, sits right after it in the
// production's strip as a narrower square, and takes its parent's colour lightened by 40% white. Colour in the UI
// always means a department: `--dept-<id>` in ui/tokens.css. Pure.

export type CoreDepartment =
  | "writing"
  | "breakdown"
  | "casting"
  | "wardrobe"
  | "sets"
  | "props"
  | "animatic"
  | "draft"
  | "final";

export type ExtraDepartment = "voice" | "music" | "sound" | "edit";

export type Department = CoreDepartment | ExtraDepartment;

/** The nine core departments, in production order: the logo, the spectrum line, marketing. */
export const CORE: readonly { id: CoreDepartment; label: string }[] = [
  { id: "writing", label: "Writing" },
  { id: "breakdown", label: "Breakdown" },
  { id: "casting", label: "Casting" },
  { id: "wardrobe", label: "Wardrobe" },
  { id: "sets", label: "Sets" },
  { id: "props", label: "Props" },
  { id: "animatic", label: "Animatic" },
  { id: "draft", label: "Draft" },
  { id: "final", label: "Final" },
];

/** The departments a production may add; each belongs to a core one (BRAND.md "Extra departments"). */
export const EXTRAS: readonly { id: ExtraDepartment; label: string; parent: CoreDepartment; what: string }[] =
  [
    {
      id: "voice",
      label: "Voices",
      parent: "casting",
      what: "cast and record the voices of your characters",
    },
    {
      id: "music",
      label: "Music",
      parent: "animatic",
      what: "score the scenes, on the timing of the animatic",
    },
    { id: "sound", label: "Sound", parent: "draft", what: "effects and atmosphere for the scenes and shots" },
    {
      id: "edit",
      label: "Edit",
      parent: "final",
      what: "assemble the cut from the picked finals and sign it off",
    },
  ];

/**
 * Every department in production order (the server's ff.department_rank): an extra sits right after its parent,
 * except the edit, which comes just before the final it belongs to.
 */
export const PRODUCTION_ORDER: readonly Department[] = [
  "writing",
  "breakdown",
  "casting",
  "voice",
  "wardrobe",
  "sets",
  "props",
  "animatic",
  "music",
  "draft",
  "sound",
  "edit",
  "final",
];

export const DEPARTMENT_LABEL = Object.fromEntries(
  [...CORE, ...EXTRAS].map((d) => [d.id, d.label]),
) as Record<Department, string>;

const PARENT = Object.fromEntries(EXTRAS.map((e) => [e.id, e.parent])) as Record<
  ExtraDepartment,
  CoreDepartment
>;

/** The CSS colour of a department. */
export const deptColor = (d: Department): string => `var(--dept-${d})`;

export const isDepartment = (d: string): d is Department => Object.hasOwn(DEPARTMENT_LABEL, d);

export const isExtra = (d: string): d is ExtraDepartment => Object.hasOwn(PARENT, d);

export const isCore = (d: string): d is CoreDepartment => isDepartment(d) && !isExtra(d);

/** The core department a department belongs to (itself, for a core one). */
export const parentOf = (d: Department): CoreDepartment => (isExtra(d) ? PARENT[d] : d);

/** Sorts departments into production order (a comparator). */
export const byProductionOrder = (a: Department, b: Department): number =>
  PRODUCTION_ORDER.indexOf(a) - PRODUCTION_ORDER.indexOf(b);

/** "Voices: cast and record the voices of your characters (with Casting)". */
export function extraLine(d: ExtraDepartment): string {
  const e = EXTRAS.find((x) => x.id === d)!;
  return `${e.label}: ${e.what} (with ${DEPARTMENT_LABEL[e.parent]})`;
}

/**
 * The extras a production can still add: those it does not run (absent, or skipped), in production order.
 * `have` is the production's steps (department and whether it is skipped).
 */
export function missingExtras(
  have: readonly { dept: Department; skipped?: boolean | undefined }[],
): ExtraDepartment[] {
  const running = new Set(have.filter((s) => !s.skipped).map((s) => s.dept));
  return EXTRAS.map((e) => e.id).filter((id) => !running.has(id));
}

/**
 * The department an element belongs to, where the mapping is plain: characters are cast, locations are
 * sets, props are props. A style has no department until the production steps exist (F4).
 */
export function elementDepartment(subtype: string | null | undefined): Department | null {
  switch (subtype) {
    case "character":
      return "casting";
    case "location":
      return "sets";
    case "prop":
      return "props";
    default:
      return null;
  }
}
